import React from 'react';
import { motion } from 'framer-motion';

const AboutSection: React.FC = () => {
  return (
    <section className="section-padding bg-black">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-4 lg:space-y-6"
          >
            <motion.h2
              className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-display font-bold text-white"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              About{' '}
              <span className="bg-gradient-to-r from-blue-400 via-red-400 to-white bg-clip-text text-transparent">
                Us
              </span>
            </motion.h2>

            <motion.p
              className="text-base md:text-lg lg:text-xl text-gray-300 leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              Ibaytech is an IT Company based in Metro Manila and Iba, Zambales dedicated on providing digital transparency; for public governance, upgrade management and community solutions.
            </motion.p>

            {/* Mission & Vision */}
            <motion.div
              className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 pt-6 lg:pt-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="glass-card p-4 lg:p-6">
                <h3 className="text-lg lg:text-xl font-semibold text-white mb-2 lg:mb-3">Our Mission</h3>
                <p className="text-gray-400 text-sm lg:text-base leading-relaxed">
                  To empower local communities through innovative technology solutions that enhance 
                  daily life and drive economic growth.
                </p>
              </div>
              
              <div className="glass-card p-4 lg:p-6">
                <h3 className="text-lg lg:text-xl font-semibold text-white mb-2 lg:mb-3">Our Vision</h3>
                <p className="text-gray-400 text-sm lg:text-base leading-relaxed">
                  To be the leading technology partner for communities, creating a digitally 
                  connected and prosperous future.
                </p>
              </div>
            </motion.div>

            {/* Key Values */}
            <motion.div
              className="pt-6 lg:pt-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl lg:text-2xl font-semibold text-white mb-4 lg:mb-6">Our Values</h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 lg:gap-4">
                {[
                  { title: 'Innovation', description: 'Cutting-edge solutions for real-world problems', color: 'blue' },
                  { title: 'Community', description: 'Local focus with global standards', color: 'red' },
                  { title: 'Excellence', description: 'Quality and reliability in every project', color: 'blue' },
                ].map((value, index) => (
                  <motion.div
                    key={index}
                    className={`text-center p-3 lg:p-4 rounded-lg border transition-colors duration-300 ${
                      value.color === 'blue' 
                        ? 'border-blue-400/20 hover:border-blue-400/40 hover:bg-blue-500/5' 
                        : 'border-red-400/20 hover:border-red-400/40 hover:bg-red-500/5'
                    }`}
                    whileHover={{ scale: 1.05 }}
                  >
                    <h4 className={`font-semibold mb-1 lg:mb-2 text-sm lg:text-base ${
                      value.color === 'blue' ? 'text-blue-400' : 'text-red-400'
                    }`}>{value.title}</h4>
                    <p className="text-gray-400 text-xs lg:text-sm">{value.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* Image/Visual Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative">
              {/* Main Image Placeholder */}
              <div className="w-full h-64 sm:h-80 lg:h-96 xl:h-[500px] bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl overflow-hidden">
                <div className="w-full h-full flex items-center justify-center">
                  <img 
                    src="/assets/IbayTech-Logo.png" 
                    alt="IbayTech Office" 
                    className="w-24 h-24 lg:w-32 lg:h-32 object-contain opacity-50"
                  />
                </div>
              </div>

              {/* Floating Cards */}
              <motion.div
                className="absolute -top-4 -left-4 lg:-top-6 lg:-left-6 glass-card p-3 lg:p-4 w-36 lg:w-48"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="text-lg lg:text-2xl font-bold text-white">2022</div>
                <div className="text-gray-400 text-xs lg:text-sm">Year Founded</div>
              </motion.div>

              <motion.div
                className="absolute -bottom-4 -right-4 lg:-bottom-6 lg:-right-6 glass-card p-3 lg:p-4 w-36 lg:w-48"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
              >
                <div className="text-lg lg:text-2xl font-bold text-white">Iba, Zambales</div>
                <div className="text-gray-400 text-xs lg:text-sm">Office</div>
              </motion.div>

              <motion.div
                className="absolute top-4 -right-4 lg:top-6 lg:-right-6 glass-card p-3 lg:p-4 w-36 lg:w-48"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 3 }}
              >
                <div className="text-lg lg:text-2xl font-bold text-white">Metro Manila</div>
                <div className="text-gray-400 text-xs lg:text-sm">Office</div>
              </motion.div>

              {/* Decorative Elements */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 lg:w-20 lg:h-20 border border-blue-400/30 rounded-full animate-ping"></div>
              <div className="absolute top-1/4 right-1/4 w-2 h-2 lg:w-3 lg:h-3 bg-red-400 rounded-full animate-pulse"></div>
              <div className="absolute bottom-1/4 left-1/4 w-1.5 h-1.5 lg:w-2 lg:h-2 bg-blue-400 rounded-full animate-pulse delay-1000"></div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
